import { twMerge } from "tailwind-merge";

export type BadgeType = "solid" | "outline";
export type BadgeSize = "sm" | "base" | "lg";
export type BadgeColor =
  | "primary"
  | "secondary"
  | "tertiary"
  | "neutral"
  | "error"
  | "success"
  | "info"
  | "warning"
  | "light";

const sizes = {
  sm: "py-px px-1.5",
  base: "py-1.5 px-2.5",
  lg: "py-3 px-4",
};

const colors = {
  primary: {
    solid: "text-primary-20",
    outline: "text-primary-80",
  },
  secondary: {
    solid: "text-secondary-20",
    outline: "text-secondary-80",
  },
  tertiary: {
    solid: "text-tertiary-20",
    outline: "text-tertiary-80",
  },
  neutral: {
    solid: "text-neutral-20",
    outline: "text-neutral-80",
  },
  error: {
    solid: "text-white",
    outline: "text-error-80",
  },
  success: {
    solid: "text-white",
    outline: "text-success-80",
  },
  info: {
    solid: "text-info-20",
    outline: "text-info-80",
  },
  warning: {
    solid: "text-warning-20",
    outline: "text-warning-80",
  },
  light: {
    solid: "text-neutral-120",
    outline: "text-neutral-80",
  },
};

const bgColors = {
  primary: {
    solid: "bg-primary-80",
    outline: "border border-primary-80",
  },
  secondary: {
    solid: "bg-secondary-80",
    outline: "border border-secondary-80",
  },
  tertiary: {
    solid: "bg-tertiary-80",
    outline: "border border-tertiary-80",
  },
  neutral: {
    solid: "bg-neutral-80",
    outline: "border border-neutral-80",
  },
  error: {
    solid: "bg-error-30",
    outline: "border border-error-80",
  },
  success: {
    solid: "bg-success-40",
    outline: "border border-success-80",
  },
  info: {
    solid: "bg-info-80",
    outline: "border border-info-80",
  },
  warning: {
    solid: "bg-complementary-orange",
    outline: "border border-warning-80",
  },
  light: {
    solid: "bg-neutral-20",
    outline: "border border-neutral-10",
  },
};

export interface BadgeProps {
  text: string;
  size?: BadgeSize;
  type?: BadgeType;
  color?: BadgeColor;
}

export const Badge = ({
  text,
  size = "base",
  type = "outline",
  color = "primary",
}: BadgeProps) => {
  const viewSize = sizes[size];
  const fontColor = colors[color][type];
  const bgColor = bgColors[color][type];

  return (
    <div className={twMerge("w-fit rounded-full", viewSize, bgColor)}>
      <span className={twMerge("text-sm", fontColor)}>{text}</span>
    </div>
  );
};
Badge.displayName = "Badge";
